/**
 * Gossl Theme - Collection Gallery
 * Handles expandable grid and Swiper-based lightbox modal.
 * Used by both collection-gallery section and content-section (gallery mode).
 *
 * Each [data-gallery-grid] is an independent gallery with its own
 * lightbox image set, toggle button, and visible item count.
 * All galleries share a single lightbox DOM element with a Swiper instance.
 */

window.Goessl = window.Goessl || {};
if (!window.Goessl._galleryLoaded) {
  window.Goessl._galleryLoaded = true;

  (function() {
    'use strict';

    class CollectionGallery {
      constructor(grid) {
        this.grid = grid;
        var nextEl = grid.nextElementSibling;
        this.toggle = nextEl ? nextEl.querySelector('[data-gallery-toggle]') : null;
        this.items = grid.querySelectorAll('[data-gallery-item]');
        this.lightbox = document.querySelector('[data-lightbox]');

        if (!this.lightbox || !this.items.length) return;

        this.isExpanded = false;
        this.currentIndex = 0;
        this.initialVisible = parseInt(this.grid.dataset.initialVisible) || 3;
        this.totalImages = this.items.length;
        this.images = [];
        this._trapHandler = null;

        this.collectImages();

        if (this.toggle && this.totalImages <= this.initialVisible) {
          var wrap = this.toggle.closest('[data-gallery-toggle-wrap]');
          if (wrap) wrap.remove();
          this.toggle = null;
        }

        this.bindEvents();
      }

      collectImages() {
        var self = this;
        this.items.forEach(function(item) {
          var img = item.querySelector('img');
          if (img) {
            self.images.push({
              src: img.dataset.fullSrc || img.src,
              alt: img.alt || ''
            });
          }
        });
      }

      bindEvents() {
        var self = this;

        if (this.toggle) {
          this.toggle.addEventListener('click', function() { self.toggleExpand(); });
        }

        this.items.forEach(function(item) {
          item.addEventListener('click', function() {
            var index = parseInt(item.dataset.galleryItem);
            self.openLightbox(index);
          });
        });

        if (!CollectionGallery._lightboxBound) {
          CollectionGallery._lightboxBound = true;
          var lb = this.lightbox;

          var closeButtons = lb.querySelectorAll('[data-lightbox-close]');
          closeButtons.forEach(function(btn) {
            btn.addEventListener('click', function() {
              if (CollectionGallery._activeInstance) CollectionGallery._activeInstance.closeLightbox();
            });
          });

          lb.addEventListener('click', function(e) {
            if (e.target === lb) {
              if (CollectionGallery._activeInstance) CollectionGallery._activeInstance.closeLightbox();
            }
          });

          document.addEventListener('keydown', function(e) {
            if (!lb.classList.contains('is-open')) return;
            if (e.key === 'Escape' && CollectionGallery._activeInstance) {
              CollectionGallery._activeInstance.closeLightbox();
            }
          });
        }
      }

      toggleExpand() {
        this.isExpanded = !this.isExpanded;
        var expanded = this.isExpanded;
        var vis = this.initialVisible;

        this.items.forEach(function(item, index) {
          if (index >= vis) {
            item.classList.toggle('is-hidden', !expanded);
          }
        });

        if (this.toggle) {
          this.toggle.setAttribute('aria-expanded', this.isExpanded);
          var moreText = this.toggle.querySelector('[data-toggle-text-more]');
          var lessText = this.toggle.querySelector('[data-toggle-text-less]');
          if (moreText && lessText) {
            moreText.hidden = expanded;
            lessText.hidden = !expanded;
          }
        }
      }

      openLightbox(index) {
        if (index < 0 || index >= this.images.length) return;

        CollectionGallery._activeInstance = this;
        this.currentIndex = index;

        this.buildSwiperSlides();
        this.initSwiper(index);

        this.lightbox.hidden = false;
        this.lightbox.classList.add('is-open');
        this.lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        var closeBtn = this.lightbox.querySelector('[data-lightbox-close]');
        if (closeBtn && closeBtn.focus) closeBtn.focus();

        var focusable = this.lightbox.querySelectorAll('button');
        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        this._trapHandler = function(e) {
          if (e.key !== 'Tab') return;
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
        this.lightbox.addEventListener('keydown', this._trapHandler);
      }

      buildSwiperSlides() {
        var slidesContainer = this.lightbox.querySelector('[data-lightbox-slides]');
        if (!slidesContainer) return;

        while (slidesContainer.firstChild) {
          slidesContainer.removeChild(slidesContainer.firstChild);
        }

        this.images.forEach(function(imgData) {
          var slide = document.createElement('div');
          slide.className = 'swiper-slide';

          var img = document.createElement('img');
          img.className = 'goessl-lightbox__image';
          img.src = imgData.src;
          img.alt = imgData.alt;
          img.draggable = false;

          slide.appendChild(img);
          slidesContainer.appendChild(slide);
        });
      }

      initSwiper(startIndex) {
        var self = this;
        var swiperEl = this.lightbox.querySelector('[data-lightbox-swiper]');
        if (!swiperEl) return;

        if (CollectionGallery._swiperInstance) {
          CollectionGallery._swiperInstance.destroy(true, true);
          CollectionGallery._swiperInstance = null;
        }

        CollectionGallery._swiperInstance = new Swiper(swiperEl, {
          initialSlide: startIndex,
          slidesPerView: 1,
          spaceBetween: 0,
          loop: this.images.length > 1,
          keyboard: { enabled: true },
          navigation: {
            nextEl: swiperEl.querySelector('.swiper-button-next'),
            prevEl: swiperEl.querySelector('.swiper-button-prev')
          },
          on: {
            slideChange: function() {
              self.updateCounter(this.realIndex);
            }
          }
        });

        this.updateCounter(startIndex);
      }

      updateCounter(index) {
        var counter = this.lightbox.querySelector('[data-lightbox-counter]');
        if (counter) {
          counter.textContent = (index + 1) + ' / ' + this.images.length;
        }
      }

      closeLightbox() {
        this.lightbox.classList.remove('is-open');
        this.lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';

        if (this._trapHandler) {
          this.lightbox.removeEventListener('keydown', this._trapHandler);
          this._trapHandler = null;
        }

        if (this.items[this.currentIndex]) {
          this.items[this.currentIndex].focus();
        }

        var lb = this.lightbox;
        setTimeout(function() {
          if (!lb.classList.contains('is-open')) {
            lb.hidden = true;
          }
          if (CollectionGallery._swiperInstance) {
            CollectionGallery._swiperInstance.destroy(true, true);
            CollectionGallery._swiperInstance = null;
          }
        }, 300);

        CollectionGallery._activeInstance = null;
      }
    }

    CollectionGallery._activeInstance = null;
    CollectionGallery._lightboxBound = false;
    CollectionGallery._swiperInstance = null;

    function initGalleries() {
      var grids = document.querySelectorAll('[data-gallery-grid]');
      grids.forEach(function(grid) {
        if (!grid.dataset.galleryInitialized) {
          grid.dataset.galleryInitialized = 'true';
          new CollectionGallery(grid);
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initGalleries);
    } else {
      initGalleries();
    }

    document.addEventListener('shopify:section:load', function(event) {
      var grids = event.target.querySelectorAll('[data-gallery-grid]');
      grids.forEach(function(grid) {
        grid.dataset.galleryInitialized = '';
        new CollectionGallery(grid);
        grid.dataset.galleryInitialized = 'true';
      });
      CollectionGallery._lightboxBound = false;
    });
  })();
}
